import Skeleton from 'antd/es/skeleton';
import Typography from 'antd/es/typography';
import React from 'react';
import styled from 'styled-components';

import {
  SectionHeader,
  SectionTitle,
} from '../../../components/TaskDescription';

import { useAgentRunQuery } from '@queries/agent-runs';
import { logger } from '@utils/logger';

const { Text } = Typography;

// Styled components
const ContentSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MetadataRow = styled.div`
  display: flex;
  margin-bottom: 8px;
  border-bottom: 1px solid ${(props) => props.theme.token.colorBorderSecondary};
  padding-bottom: 8px;

  &:last-child {
    border-bottom: none;
  }
`;

const MetadataLabel = styled(Text)`
  font-weight: 500;
  min-width: 120px;
`;

// Format timestamps
const formatDate = (dateString: string | Date, agentRunId?: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch (error) {
    logger.error({ agentRunId }, 'Error formatting date', { error });
    return 'Unknown date';
  }
};

interface AgentRunDetailsProps {
  agentStatus: string;
  agentRunId: string;
}

const AgentRunDetails: React.FC<AgentRunDetailsProps> = ({
  agentStatus,
  agentRunId,
}) => {
  const { data: agentRun, isLoading } = useAgentRunQuery(agentRunId);

  if (isLoading) {
    return <Skeleton active />;
  }

  if (!agentRun) {
    return null;
  }

  return (
    <ContentSection>
      <SectionHeader>
        <SectionTitle level={4} style={{ margin: 0 }}>
          Details
        </SectionTitle>
      </SectionHeader>
      <MetadataRow>
        <MetadataLabel>Status:</MetadataLabel>
        <Text>{agentStatus}</Text>
      </MetadataRow>
      <MetadataRow>
        <MetadataLabel>Created:</MetadataLabel>
        <Text>{formatDate(agentRun.createdAt, agentRunId)}</Text>
      </MetadataRow>
      <MetadataRow>
        <MetadataLabel>Last updated:</MetadataLabel>
        <Text>{formatDate(agentRun.updatedAt, agentRunId)}</Text>
      </MetadataRow>
      {agentRun.failedAt && (
        <MetadataRow>
          <MetadataLabel>Failed:</MetadataLabel>
          <Text>{formatDate(agentRun.failedAt, agentRunId)}</Text>
        </MetadataRow>
      )}
    </ContentSection>
  );
};

export default AgentRunDetails;
